import { useCallback, useEffect, useState } from 'react'

import { IFsNodeSchema } from '@cloudike/web_files'
import {
  areAllAssetsFetchedSelector,
  selectedAssetsIdsSelector,
  selectSortedFileAssets,
  systemFieldsSelector
} from 'features/assets/model/selectors/assetsSelectors'
import {
  assetsActions,
  downloadAssetsThunk,
  fetchFirstAssetsThunk,
  fetchNextAssetsThunk,
  fetchSystemFieldsThunk
} from 'features/assets/model/slice/assetsSlice'
import { FILES_EXTANSIONS_FOR_AI_ASSISTANT_INDEXING, IFieldOption } from 'features/assets/model/types/assets'
import { AssetsGrid } from 'features/assets/ui'
import { getFilteredBoardsSelector, getSystemFields, selectedBoardsIdsSelector } from 'features/boards/model/selectors/boardsSelectors'
import { boardsSelectors } from 'features/boards/model/slice/boardsSlice'
import { BoardsGrid } from 'features/boards/ui/BoardsGrid'
import { setUploadFilesForTargetBoardModal } from 'features/boards/ui/SelectTargetBoardToUploadModal'
import { useGetToolbarExtraActions } from 'features/common/hooks/useGetToolbarExtraActions'
import { useGetToolbarMainActions } from 'features/common/hooks/useGetToolbarMainActions'
import { useOnClickOutsideCards } from 'features/common/hooks/useOnClickOutsideCards'
import { useSearchUrlParams } from 'features/common/hooks/useSearchUrlParams'
import {
  selectSearchAssetsSelector,
  selectSearchBoardsSelector,
  selectSearchIsSearchPageSelector,
  selectSearchSelectedItemsIdsSelector
} from 'features/common/model/selectors/searchSelectors'
import { setIsSelectTargetBoardToUploadModalOpen } from 'features/common/model/slice/appSlice'
import { openModal } from 'features/common/model/slice/moadlSlice'
import {
  clearSearchState,
  fetchLabels,
  selectItem,
  selectItemWithPressedCtrl,
  selectItemWithPressedShift,
  setSelectedItemsIds,
  unselectAll
} from 'features/common/model/slice/searchSlice'
import { sharingActions } from 'features/common/model/slice/sharingSlice'
import { AdjustableSection, ItemsViewToolbar, SortingControl } from 'features/common/ui/dashboard'
import { useScrollContainer } from 'features/common/ui/dashboard/Layout'
import { EmptyBoardPlaceholder } from 'features/common/ui/EmptyBoardPlaceholder'
import { MultiSelectTool } from 'features/common/ui/MultiSelectTool'
import { SkeletonAllAssets } from 'features/common/ui/SkeletonAllAssets'
import { getFilesPreviewIsPublicLinkSelector, getFilesPreviewItemsSelector } from 'features/files-preview/model/selectors/filesPreviewSelectors'
import { useGetEnablePermission } from 'features/public-link-files/hooks/useGetEnablePermission'
import { MemberRole } from 'features/team/model/types/members'
import { assetRootBoardIdSelector, getUserRole } from 'features/user/model/selectors/userSelectors'
import { useInView } from 'react-intersection-observer'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { ROUTES } from 'shared/constants/routesPaths'
import { SDK_TYPES } from 'shared/sdk/sdkConstants'
import { AppstoreAddOutlined, ArrowRightOutlined, CopyOutlined, DeploymentUnitOutlined, LinkOutlined, MinusCircleOutlined, ShareAltOutlined, Spin } from 'shared/ui'
import { hasAccess } from 'shared/utils/access'
import styled from 'styled-components'
import { UseAssetForAiAssistantSwitch } from 'features/ai-assistant/ui/UseAssetForAiAssistantSwitch'
import { useTranslation } from 'react-i18next'
import { BoardToolbarAction, BoardAssetsToolbarAction } from 'shared/constants/toolbar'

import { loadAllAssetsInBackgroundThunk } from '../../features/assets/model/slice/assetsSlice'
import { boardsActions, downloadBoardsThunk, getAvailableToolbarActionsByUserRole } from '../../features/boards/model/slice/boardsSlice'
import { useSorting } from '../../features/common/hooks'
import { selectSelectedSort, selectSortOrder } from '../../features/common/model/selectors/appSelectors'
import { ToolbarTop } from '../../features/common/ui/dashboard/ToolbarTop'
import { DropzoneArea } from '../../features/dropzone/DropzoneArea'
import {
  dropzoneActions,
  removeUploadHandlerThunk,
  setUploadHandlerThunk
} from '../../features/dropzone/dropzoneSlice'
import { getIsDropzoneAreaVisibleSelector } from '../../features/dropzone/selectors'
import { filesPreviewActions, openFilesPreviewThunk } from '../../features/files-preview/model/slice/filesPreviewSlice'
import { ArrowsAltOutlined, DeleteOutlined, DownloadOutlined, EditOutlined } from '../../shared/ui'

type ContextItemType = 'board' | 'asset' | null

export const AllAssetsPage = () => {
  const dispatch = useAppDispatch()
  const isAssetInTrash = (item) => item.linked_node_is_trashed || item.is_trashed || item.is_explicitly_trashed
  const { t } = useTranslation()

  const allBoards = useAppSelector(getFilteredBoardsSelector)
  const allVisibleSortedAssets = useAppSelector(selectSortedFileAssets).filter(asset => !isAssetInTrash(asset))

  const boards = useAppSelector(state => boardsSelectors.selectAll(state.boards))
  const selectedAssetsIds = useAppSelector(selectedAssetsIdsSelector)
  const assetRootBoardId = useAppSelector(assetRootBoardIdSelector)
  const areAllAssetsFetched = useAppSelector(areAllAssetsFetchedSelector)
  const userRole = useAppSelector(getUserRole)
  const isPublicLink = useAppSelector(getFilesPreviewIsPublicLinkSelector)
  const { isDownloadEnabled, isEditContentEnabled } = useGetEnablePermission()
  const availableAssetsActions = getAvailableToolbarActionsByUserRole(userRole, isDownloadEnabled, isEditContentEnabled, isPublicLink)
  const selectedAssets = allVisibleSortedAssets.filter(asset => selectedAssetsIds.includes(asset.id))
  const isSearch = useAppSelector(selectSearchIsSearchPageSelector)
  const searchBoards = useAppSelector(selectSearchBoardsSelector)
  const searchAssets = useAppSelector(selectSearchAssetsSelector)
  const systemFieldsByAssets = useAppSelector(systemFieldsSelector)
  const systemFieldsByBoards = useAppSelector(getSystemFields)
  const selectedBoardsIds = useAppSelector(selectedBoardsIdsSelector)
  const searchSelectedItemsIds = useAppSelector(selectSearchSelectedItemsIdsSelector)
  const selectedBoards = allBoards.filter(board => selectedBoardsIds.includes(board.id))
  const isPreviewModalOpened = useAppSelector(getFilesPreviewItemsSelector).length > 0
  const isDropzoneAreaVisible = getIsDropzoneAreaVisibleSelector()
  const sortType = useAppSelector(selectSelectedSort)
  const sortOrder = useAppSelector(selectSortOrder)
  const [searchParams] = useSearchParams()

  const selectedAssetsTargetClassName = 'js-asset-card'
  const selectedSearchAssetsTargetClassName = 'search-asset-card'
  const selectedSearchBoardsTargetClassName = 'search-board-card'

  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)

  const {
    sortingOptions,
    currentSortValue,
    handleSortChange
  } = useSorting()

  useEffect(() => {
    dispatch(fetchSystemFieldsThunk())
    dispatch(fetchLabels())
  },[])

  // Use the new hook for handling search URL parameters
  useSearchUrlParams({
    systemFieldsByAssets,
    systemFieldsByBoards
  })

  const hasMixedSelection = isSearch &&
    searchSelectedItemsIds.some(id => searchBoards.some(board => board.id === id)) &&
    searchSelectedItemsIds.some(id => searchAssets?.some(asset => asset.id === id))

  const excludedMainAssetsActions = [
    BoardAssetsToolbarAction.Open,
    BoardAssetsToolbarAction.RemoveFromBoard,
    // BoardAssetsToolbarAction.MakeFavorite,
    BoardAssetsToolbarAction.Rename,
    BoardAssetsToolbarAction.Copy,
    BoardAssetsToolbarAction.AddToAnotherBoard,
    BoardAssetsToolbarAction.Move,
  ]

  const assetsToolbarMainActions = useGetToolbarMainActions({ availableActions: availableAssetsActions, excludedActions: excludedMainAssetsActions })
  const actionsButtonCustomStyles = {}
  const toolbarActionsIcons = {
    [BoardToolbarAction.Rename]: <EditOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Copy]: <CopyOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Delete]: <DeleteOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Download]: <DownloadOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.Share]: <ShareAltOutlined style={actionsButtonCustomStyles} />,
    [BoardToolbarAction.AddToAnotherBoard]: <AppstoreAddOutlined style={actionsButtonCustomStyles} />,
  }

  const contextMenuActionsIcons = {
    [BoardToolbarAction.Open]: <ArrowsAltOutlined />,
    [BoardToolbarAction.Share]: <LinkOutlined />,
    // [BoardToolbarAction.MakeFavorite]: <StarOutlined />,
    [BoardToolbarAction.Download]: <DownloadOutlined />,
    [BoardToolbarAction.Copy]: <CopyOutlined />,
    [BoardToolbarAction.Move]: <ArrowRightOutlined />,
    [BoardToolbarAction.Rename]: <EditOutlined />,
    [BoardToolbarAction.RemoveFromBoard]: <MinusCircleOutlined />,
    [BoardToolbarAction.Delete]: <DeleteOutlined />,
    [BoardAssetsToolbarAction.AddToAnotherBoard]: <AppstoreAddOutlined />,
  }

  const extraActions = [
    [{
      key: BoardToolbarAction.Open,
      label: 'Open',
      icon: contextMenuActionsIcons[BoardToolbarAction.Open],
    },
    {
      key: BoardToolbarAction.Share,
      label: 'Share link',
      icon: contextMenuActionsIcons[BoardToolbarAction.Share],
    },
    {
      key: BoardToolbarAction.MakeFavorite,
      label: 'Make favorite',
      icon: contextMenuActionsIcons[BoardToolbarAction.MakeFavorite],
    }
    ],
    [{
      key: BoardAssetsToolbarAction.Download,
      label: 'Download',
      icon: contextMenuActionsIcons[BoardToolbarAction.Download],
    },
    {
      key: BoardToolbarAction.Copy,
      label: 'Copy to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Copy],
    },
    {
      key: BoardToolbarAction.Move,
      label: 'Move to board',
      icon: contextMenuActionsIcons[BoardToolbarAction.Move],
    }],
    [
      {
        key: BoardAssetsToolbarAction.AddToAnotherBoard,
        label: 'Add to board',
        icon: contextMenuActionsIcons[BoardAssetsToolbarAction.AddToAnotherBoard],
      },
      {
        key: BoardToolbarAction.Rename,
        label: 'Rename',
        icon: contextMenuActionsIcons[BoardToolbarAction.Rename],
      },
      {
        key: BoardToolbarAction.RemoveFromBoard,
        label: 'Remove from board',
        icon: contextMenuActionsIcons[BoardToolbarAction.RemoveFromBoard],
      },
      {
        key: BoardToolbarAction.Delete,
        label: 'Delete',
        icon: contextMenuActionsIcons[BoardToolbarAction.Delete],
        danger: true,
      }
    ]
  ]

  const excludedExtraAssetsActions = [
    BoardToolbarAction.Share,
    BoardToolbarAction.Download,
    BoardToolbarAction.Move,
    BoardToolbarAction.Copy,
    BoardToolbarAction.RemoveFromBoard,
    BoardToolbarAction.Delete,
    BoardToolbarAction.MakeFavorite,
  ]

  const assetsToolbarExtraActions = useGetToolbarExtraActions({ availableActions: extraActions, excludedActions: excludedExtraAssetsActions })

  const contextMenuItems = useCallback((asset: IFsNodeSchema) => [
    {
      key: BoardAssetsToolbarAction.Open,
      label: 'Open',
      icon: <ArrowsAltOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.Share,
      label: 'Share link',
      icon: <LinkOutlined />,
    },
    // {
    //   key: BoardAssetsToolbarAction.MakeFavorite,
    //   label: 'Add to favorite',
    //   icon: <StarOutlined />,
    // },
    {
      type: 'divider' as const,
    },
    {
      key: BoardAssetsToolbarAction.Download,
      label: 'Download',
      icon: <DownloadOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.Rename,
      label: 'Rename',
      icon: <EditOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && FILES_EXTANSIONS_FOR_AI_ASSISTANT_INDEXING.includes(asset.ext) && !isPublicLink && {
      key: BoardToolbarAction.AllowForAiAssistant,
      label: t('a_common_allowAivard'),
      icon: <DeploymentUnitOutlined />,
      extra: (
        <div onClick={(e) => e.stopPropagation()}>
          <UseAssetForAiAssistantSwitch itemId={asset.id} />
        </div>
      ),
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.AddToAnotherBoard,
      label: 'Add to board',
      icon: <AppstoreAddOutlined />
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      type: 'divider' as const,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      key: BoardAssetsToolbarAction.Delete,
      label: 'Delete from library',
      icon: <DeleteOutlined />,
      danger: true,
    }
  ].filter(Boolean), [userRole, isPublicLink])

  const contextMenuItemsBoard = [
    {
      key: BoardAssetsToolbarAction.Open,
      label: 'Open',
      icon: <ArrowsAltOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.Share,
      label: 'Share link',
      icon: <LinkOutlined />,
    },
    // {
    //   key: BoardAssetsToolbarAction.MakeFavorite,
    //   label: 'Add to favorite',
    //   icon: <StarOutlined />,
    // },
    {
      type: 'divider' as const,
    },
    {
      key: BoardAssetsToolbarAction.Download,
      label: 'Download',
      icon: <DownloadOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardToolbarAction.Copy,
      label: 'Copy to board',
      icon: <CopyOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && {
      key: BoardAssetsToolbarAction.Rename,
      label: 'Rename',
      icon: <EditOutlined />,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      type: 'divider' as const,
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && {
      key: BoardAssetsToolbarAction.Delete,
      label: 'Delete from library',
      icon: <DeleteOutlined />,
      danger: true,
    }
  ].filter(Boolean)

  useEffect(() => {
    return () => {
      dispatch(assetsActions.unselectAll())
    }
  }, [])

  const handleUnselectAll = useCallback(() => {
    if (isSearch) {
      dispatch(unselectAll())
    } else {
      dispatch(assetsActions.unselectAll())
    }
  }, [dispatch, isSearch])

  useOnClickOutsideCards(handleUnselectAll)

  const [withPressedCtrl, setWithPressedCtrl] = useState(false)
  const [withPressedShift, setWithPressedShift] = useState(false)
  const [isMouseSelecting, setIsMouseSelecting] = useState<boolean>(false)

  const { ref: loaderBoxRef, inView: isLoaderInView } = useInView()

  useEffect(() => {
    if (allVisibleSortedAssets.length && isLoaderInView) {
      dispatch(fetchNextAssetsThunk())
    }
  }, [isLoaderInView, allVisibleSortedAssets])

  useEffect(() => {
    if(!assetRootBoardId) return
    setIsLoading(true)
    dispatch(fetchFirstAssetsThunk())
      .finally(() => setIsLoading(false))
  }, [assetRootBoardId])

  useEffect(() => {
    if(isLoading) return
    dispatch(fetchFirstAssetsThunk())
  }, [sortType, sortOrder])

  // dropzone settings
  useEffect(() => {
    dispatch(setUploadHandlerThunk({ callback: handleUploadFiles }))
    dispatch(dropzoneActions.updateDropzoneOptions({
      disabled: userRole === MemberRole.GUEST ? true : false
    }))
    dispatch(dropzoneActions.toggleDropzoneAreaVisibility(true))

    return () => {
      dispatch(removeUploadHandlerThunk())
      dispatch(dropzoneActions.updateDropzoneOptions({
        disabled: true
      }))
    }
  }, [userRole])

  useEffect(() => {
    if (!isPreviewModalOpened) return

    const fileId = searchParams.get('prv')

    if (fileId && assetRootBoardId) {
      dispatch(loadAllAssetsInBackgroundThunk({
        currentFileId: fileId,
        callback: (items, currentIndex) => {
          dispatch(filesPreviewActions.updatePreviewWithBackgroundData({
            items,
            currentIndex,
            totalItemsCount: items.length
          }))
        }
      }))
    }
  }, [assetRootBoardId, isPreviewModalOpened])

  const handleSelect = (node: IFsNodeSchema) => {
    if (isSearch) {
      if (withPressedCtrl) {
        dispatch(selectItemWithPressedCtrl({ id: node.id }))
      } else if (withPressedShift) {
        const items = node.type === 'dir' ? searchBoards : searchAssets
        dispatch(selectItemWithPressedShift({ id: node.id, items }))
      } else {
        dispatch(selectItem({ id: node.id }))
      }
    } else {
      if (withPressedCtrl) {
        dispatch(assetsActions.selectItemWithPressedCtrl({ id: node.id }))
      } else if (withPressedShift) {
        dispatch(assetsActions.selectItemWithPressedShift({ id: node.id, items: allVisibleSortedAssets }))
      } else {
        dispatch(assetsActions.selectItem({ id: node.id }))
      }
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setWithPressedCtrl(true)
      }
      if (event.key === 'Shift') {
        setWithPressedShift(true)
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Meta' || event.key === 'Control') {
        setWithPressedCtrl(false)
      }
      if (event.key === 'Shift') {
        setWithPressedShift(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [])

  const handleClickOutside = () => {
    // Пустая функция для соблюдения интерфейса
  }

  const handleAssetsContextActionClick = (action: BoardAssetsToolbarAction, itemId: string) => {
    const items = isSearch
      ? ((searchAssets || []) as IFsNodeSchema[])
      : (allVisibleSortedAssets as IFsNodeSchema[])
    handleContextActionClick(action as unknown as BoardToolbarAction, itemId, items, 'asset')
  }

  const handleBoardContextActionClick = (action: BoardToolbarAction, itemId: string) => {
    const items = isSearch
      ? (searchBoards as IFsNodeSchema[])
      : (boards as IFsNodeSchema[])
    handleContextActionClick(action, itemId, items, 'board')
  }

  function handleUploadFiles (files: FileList) {
    if (files && files.length > 0) {
      setUploadFilesForTargetBoardModal(files)
      dispatch(setIsSelectTargetBoardToUploadModalOpen(true))
    }
  }

  const handleDownload = ({ ids, itemType }: { ids: string[], itemType: ContextItemType }) => {
    if(ids && ids.length) {
      if(itemType === 'board') {
        dispatch(downloadBoardsThunk({ ids }))
      } else {
        dispatch(downloadAssetsThunk({ ids }))
      }
    }
  }

  const handleContextActionClick = (
    action: BoardToolbarAction,
    itemId: string,
    items: IFsNodeSchema[],
    itemType: ContextItemType
  ) => {
    const targetItem = items.find(item => item.id === itemId)
    if (!targetItem) return

    switch (action) {
    case BoardToolbarAction.Open:
      dispatch(assetsActions.unselectAll())
      dispatch(boardsActions.unselectAll())
      break
    case BoardToolbarAction.Rename:
      dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Copy:
    case BoardToolbarAction.Move:
    case BoardToolbarAction.AddToAnotherBoard:
      dispatch(openModal({ type: action, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Delete:
      dispatch(openModal({ type: BoardToolbarAction.Delete, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.RemoveFromBoard:
      dispatch(openModal({ type: BoardToolbarAction.RemoveFromBoard, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      break
    case BoardToolbarAction.Download:
      handleDownload({ ids: [targetItem?.id], itemType: itemType })
      break
    case BoardToolbarAction.Share:
      dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [targetItem?.id], items: [targetItem] } }))
      dispatch(sharingActions.setSharingAlbumConfig({
        sdkType: SDK_TYPES.FILES
      }))
      dispatch(sharingActions.setSharedItemData(targetItem))
      break
    }
  }

  const handleToolbarActionClick = (
    itemType: ContextItemType,
    actionId: BoardToolbarAction,
    items: any[]
  ) => {
    if (isSearch) {
      const selectedSearchAssets = searchAssets?.filter(asset => searchSelectedItemsIds.includes(asset.id)) || []
      const selectedSearchBoards = searchBoards.filter(board => searchSelectedItemsIds.includes(board.id))

      switch (actionId) {
      case BoardToolbarAction.Open:
        if(items.length === 1) {
          const asset = items[0] as IFsNodeSchema
          if (asset.type === 'file' || asset.type as string === 'link') {
            dispatch(openFilesPreviewThunk({
              items: searchAssets as any,
              currentItemId: asset.id,
              totalItemsCount: 1,
              navigate
            }))
          } else if (asset.type === 'dir') {
            navigate(`${ROUTES.dashboard.board}/${asset.id}`)
          }
        }
        dispatch(unselectAll())
        break
      case BoardToolbarAction.Rename:
        dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [items[0].id], items: [items[0]] } }))
        break
      case BoardToolbarAction.Copy:
      case BoardToolbarAction.Move:
      case BoardToolbarAction.AddToAnotherBoard:
        dispatch(openModal({
          type: actionId,
          context: itemType,
          data: {
            ids: searchSelectedItemsIds,
            items: [...selectedSearchAssets, ...selectedSearchBoards]
          }
        }))
        break
      case BoardToolbarAction.Delete:
        dispatch(openModal({
          type: BoardToolbarAction.Delete,
          context: itemType,
          data: {
            ids: searchSelectedItemsIds,
            items: [...selectedSearchAssets, ...selectedSearchBoards]
          }
        }))
        break
      case BoardToolbarAction.Download:
        const assetIds = selectedSearchAssets.map(asset => asset.id)
        const boardIds = selectedSearchBoards.map(board => board.id)
        if (boardIds.length > 0 || assetIds.length > 0) {
          handleDownload({ ids: [...boardIds, ...assetIds], itemType: itemType })
        }
        break
      case BoardToolbarAction.Share:
        if (selectedSearchAssets.length === 1) {
          dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [selectedSearchAssets[0].id], items: [selectedSearchAssets[0]] } }))
          dispatch(sharingActions.setSharingAlbumConfig({
            sdkType: SDK_TYPES.FILES
          }))
          dispatch(sharingActions.setSharedItemData(selectedSearchAssets[0]))
        } else if (selectedSearchBoards.length === 1) {
          dispatch(openModal({ type: BoardToolbarAction.Share, context: 'board', data: { ids: [selectedSearchBoards[0].id], items: [selectedSearchBoards[0]] } }))
          dispatch(sharingActions.setSharingAlbumConfig({
            sdkType: SDK_TYPES.FILES
          }))
          dispatch(sharingActions.setSharedItemData(selectedSearchBoards[0]))
        }
        break
      }
    } else {
      switch (actionId) {
      case BoardToolbarAction.Open:
        if(items.length === 1) {
          const asset = items[0] as IFsNodeSchema
          dispatch(openFilesPreviewThunk({
            items: allVisibleSortedAssets as any,
            currentItemId: asset.id,
            totalItemsCount: 1,
            navigate
          }))
        }
        dispatch(assetsActions.unselectAll())
        dispatch(boardsActions.unselectAll())
        break
      case BoardToolbarAction.Rename:
        dispatch(openModal({ type: BoardToolbarAction.Rename, context: itemType, data: { ids: [items[0].id], items: [items[0]] } }))
        break
      case BoardToolbarAction.Copy:
      case BoardToolbarAction.Move:
      case BoardToolbarAction.AddToAnotherBoard:
        dispatch(openModal({ type: actionId, context: itemType, data: { ids: [...selectedAssetsIds, ...selectedBoardsIds], items: [...selectedAssets, ...selectedBoards] } }))
        break
      case BoardToolbarAction.Delete:
        dispatch(openModal({ type: BoardToolbarAction.Delete, context: itemType, data: { ids: [...selectedAssetsIds, ...selectedBoardsIds], items: [...selectedAssets, ...selectedBoards] } }))
        break
      case BoardToolbarAction.Download:
        if (selectedBoardsIds.length > 0 || selectedAssetsIds.length > 0) {
          handleDownload({ ids: [...selectedBoardsIds, ...selectedAssetsIds], itemType: itemType })
        }
        break
      case BoardToolbarAction.Share:
        dispatch(openModal({ type: BoardToolbarAction.Share, context: itemType, data: { ids: [selectedAssetsIds[0]], items: [selectedAssets[0]] } }))
        dispatch(sharingActions.setSharingAlbumConfig({
          sdkType: SDK_TYPES.FILES
        }))
        dispatch(sharingActions.setSharedItemData(selectedAssets[0]))
        break
      }
    }
  }

  const handleCloseToolbar = () => {
    if (isSearch) {
      dispatch(unselectAll())
    } else {
      dispatch(assetsActions.unselectAll())
    }
  }
  const openBoard = (board: IFsNodeSchema) => {
    navigate(`${ROUTES.dashboard.board}/${board.id}`)
  }

  const handleBoardDoubleClick = (board: IFsNodeSchema) => {
    openBoard(board)
  }

  useEffect(() => {
    return () => {
      if(isSearch) {
        dispatch(clearSearchState())
      }
    }
  }, [isSearch])

  const handleMouseSelect = (event: any, result: { id: string, targetClassName: string }[]) => {
    const selectedItems = result.map((item) => {
      if (item.targetClassName === selectedSearchBoardsTargetClassName) {
        const board = searchBoards.find((board) => board.id === item.id)
        return {
          ...board,
          itemType: 'board',
        }
      } else if (item.targetClassName === selectedAssetsTargetClassName) {
        const asset = allVisibleSortedAssets.find((asset) => asset.id === item.id)
        return {
          ...asset,
          itemType: 'asset',
        }
      } else if (item.targetClassName === selectedSearchAssetsTargetClassName) {
        const searchAsset = searchAssets.find((asset) => asset.id === item.id)
        return {
          ...searchAsset,
          itemType: 'asset',
        }
      }
    })
    const selectedAssets = selectedItems.filter((item) => item?.itemType === 'asset')
    const selectedBoards = selectedItems.filter((item) => item?.itemType === 'board')

    if (isSearch) {
      const selectedIds = [...selectedAssets, ...selectedBoards].map(item => item?.id).filter(id => !!id)
      if (selectedIds.length) {
        dispatch(setSelectedItemsIds(selectedIds))
      }
    } else {
      if (selectedAssets.length || selectedBoards.length) {
        const selectedAssetsIds = selectedAssets.map((asset) => asset?.id).filter(id => !!id)
        const selectedBoardsIds = selectedBoards.map((board) => board?.id).filter(id => !!id)
        dispatch(boardsActions.setSelectedItemsIds(selectedBoardsIds))
        dispatch(assetsActions.setSelectedItemsIds(selectedAssetsIds))
      }
    }
  }

  const handleMouseSelectEnd = () => {
    setIsMouseSelecting(false)
  }

  const handleMouseSelectStart = () => {
    setIsMouseSelecting(true)
  }

  const scrollContainerRef = useScrollContainer()

  return (
    <div>
      {isLoading ? (
        <SkeletonAllAssets />
      ) : (
        <>
          {isSearch ? (
            <>
              <ToolbarTop
                isShown={searchSelectedItemsIds.length > 0}
                itemsType={searchSelectedItemsIds.some(id =>
                  searchBoards.some(board => board.id === id)) ? 'board' : 'asset'}
                mainActions={assetsToolbarMainActions}
                extraActionsGroups={assetsToolbarExtraActions}
                selectedItems={[
                  ...searchBoards.filter(board => searchSelectedItemsIds.includes(board.id)),
                  ...searchAssets?.filter(asset => searchSelectedItemsIds.includes(asset.id)) || []
                ]}
                actionsIcons={toolbarActionsIcons}
                onActionClick={handleToolbarActionClick}
                onClose={handleCloseToolbar}
                multiselectSupportedActions={[
                  BoardToolbarAction.Move,
                  BoardToolbarAction.Copy,
                  BoardToolbarAction.Download,
                  ...(hasMixedSelection ? [] : [BoardToolbarAction.AddToAnotherBoard]),
                ]}
              />

              {searchBoards.length > 0 && (
                <AdjustableSection
                  title={searchAssets.length === 0 ? "" : "Boards"}
                >
                  <BoardsGrid
                    boards={searchBoards as IFsNodeSchema[]}
                    onSelect={handleSelect}
                    selectedItemsIds={searchSelectedItemsIds}
                    onClickOutside={handleClickOutside}
                    onBoardDoubleClick={handleBoardDoubleClick}
                    onMenuItemClick={handleBoardContextActionClick}
                    contextMenuItems={contextMenuItemsBoard}
                    selectedTargetClassName={selectedSearchBoardsTargetClassName}
                    onContextStateChanged={() => false}
                    isOuterContextMenuOpened={false}
                  />
                </AdjustableSection>
              )}

              {searchAssets.length > 0 && (
                <SAdjustableSectionAssets
                  title={searchBoards.length === 0 ? "" : "Assets"}
                >
                  <AssetsGrid
                    assets={searchAssets as any}
                    onSelect={handleSelect}
                    selectedItemsIds={searchSelectedItemsIds}
                    onClickOutside={handleClickOutside}
                    onMenuItemClick={handleAssetsContextActionClick}
                    contextMenuItems={contextMenuItems}
                    isItemHoverEnabled={!isMouseSelecting}
                    selectedTargetClassName={selectedSearchAssetsTargetClassName}
                    onDoubleClick={handleUnselectAll}
                    onContextStateChanged={() => false}
                    isOuterContextMenuOpened={false}
                  />
                </SAdjustableSectionAssets>
              )}

              {
                searchAssets.length === 0 && searchBoards.length === 0 && (
                  <EmptyBoardPlaceholder
                    texts={{
                      title: 'Nothing found',
                      description: 'Please check for typos or filters, or try a different search query.'
                    }}
                  />
                )
              }
            </>
          ) : (
            <>
              <ToolbarTop
                isShown={selectedAssetsIds.length > 0}
                itemsType="asset"
                mainActions={assetsToolbarMainActions}
                extraActionsGroups={assetsToolbarExtraActions}
                selectedItems={selectedAssets}
                actionsIcons={toolbarActionsIcons}
                onActionClick={handleToolbarActionClick}
                onClose={handleCloseToolbar}
                multiselectSupportedActions={[
                  BoardToolbarAction.Move,
                  BoardToolbarAction.Copy,
                  BoardToolbarAction.Delete,
                  BoardToolbarAction.Download,
                  BoardToolbarAction.AddToAnotherBoard,
                  // BoardToolbarAction.MakeFavorite
                ]}
              />

              {!!allVisibleSortedAssets.length && (
                <AdjustableSection>
                  <ItemsViewToolbar>
                    <SortingControl
                      options={sortingOptions}
                      value={currentSortValue}
                      onChange={handleSortChange}
                      placeholder="Sort by"
                    />
                  </ItemsViewToolbar>

                  <AssetsGrid
                    assets={allVisibleSortedAssets}
                    onSelect={handleSelect}
                    selectedItemsIds={selectedAssetsIds}
                    onClickOutside={handleClickOutside}
                    onMenuItemClick={handleAssetsContextActionClick}
                    contextMenuItems={contextMenuItems}
                    isItemHoverEnabled={!isMouseSelecting}
                    onDoubleClick={handleUnselectAll}
                    onContextStateChanged={() => false}
                    isOuterContextMenuOpened={false}
                  />

                  {!areAllAssetsFetched && !!allVisibleSortedAssets.length && (
                    <SLoaderRow ref={loaderBoxRef}>
                      <SSpinner  />
                    </SLoaderRow>
                  )}
                </AdjustableSection>
              )}

              {!allVisibleSortedAssets.length && (
                <EmptyBoardPlaceholder
                  texts={{
                    title: 'No assets',
                    description: 'Upload or import assets to get started'
                  }}
                />
              )}
            </>
          )}
        </>
      )}

      <MultiSelectTool
        onSelect={handleMouseSelect}
        onSelectStart={handleMouseSelectStart}
        onSelectEnd={handleMouseSelectEnd}
        selectedTargetClassNames={[
          selectedAssetsTargetClassName,
          selectedSearchAssetsTargetClassName,
          selectedSearchBoardsTargetClassName,
        ]}
        scrollRef={scrollContainerRef}
      />

      <StyledDropzoneArea
        visible={isDropzoneAreaVisible}
        text={'The files will be uploaded to the assets'}
      />
    </div>
  )
}

const SAdjustableSectionAssets = styled(AdjustableSection)`
  margin-top: 20px;
`

const SLoaderRow = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const SSpinner = styled(Spin)``

const StyledDropzoneArea = styled(DropzoneArea)`
  position: fixed;
  top: 70px;
  left: 270px;
  width: calc(100vw - 283px);
  height: calc(100vh - 75px);
  z-index: 1000;
  pointer-events: none;
`
